/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Intent: fraccionar producto madre en cortes (disassemble BOM).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import { formatQtyCompact, formatUnitLabel, normalizeUnitOfMeasure } from '@shared/quantity';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { OperationalScrollScreen } from '../../shared/ticket/OperationalScrollScreen';
import { PaperSheet } from '../../shared/ticket/PaperSheet';
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { showError, showSuccess } from '../../shared/ui/appToast';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { useTabWorkspaceLayout } from '../../shared/layout/useContentLayout';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { getCatalogRamSnapshotCache } from '../infra/catalogRamSnapshotCache';
import {
  applyRecipeDisassemble,
  getCachedBranchProductQty,
  getRecipeForProduct,
  type RecipeDto,
} from '../infra/recipesRemoteRepo';
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal';
import { parseDecimalInput } from '../../shared/numeric/public-api';

type Params = {
  DisassembleRecipe: {
    productId: string;
    productName?: string;
  };
};

function uomLabel(raw: string | null | undefined): string {
  return formatUnitLabel(normalizeUnitOfMeasure(raw));
}

export const DisassembleRecipeScreen = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<RouteProp<Params, 'DisassembleRecipe'>>();
  const productId = route.params?.productId ?? '';
  const productName = route.params?.productName ?? 'Producto';
  const hasRecipes = useHasBusinessCapability('recipes');
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const { layoutWide } = useTabWorkspaceLayout();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recipe, setRecipe] = useState<RecipeDto | null>(null);
  const [batchQty, setBatchQty] = useState('1');
  const [negStockConfirmOpen, setNegStockConfirmOpen] = useState(false);

  const motherUnit = useMemo(() => {
    const products = getCatalogRamSnapshotCache()?.products ?? [];
    const row = products.find((p) => String(p.id) === productId);
    return uomLabel(row?.unit_of_measure);
  }, [productId]);

  const uomById = useMemo(() => {
    const products = getCatalogRamSnapshotCache()?.products ?? [];
    return new Map(products.map((p) => [String(p.id), uomLabel(p.unit_of_measure)] as const));
  }, [recipe]);

  const load = useCallback(async () => {
    if (!productId || !hasRecipes) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const row = await getRecipeForProduct(productId, 'disassemble');
      setRecipe(row);
      if (row) setBatchQty(String(row.yieldQty || 1).replace('.', ','));
    } catch (error) {
      presentUnexpectedFailure({
        error,
        module: 'catalog',
        op: 'recipes.disassemble.load',
        userMessage: 'No pude cargar el fraccionamiento. Reintentá.',
      });
    } finally {
      setLoading(false);
    }
  }, [productId, hasRecipes]);

  useEffect(() => {
    void load();
  }, [load]);

  const qtyNum = parseDecimalInput(batchQty) ?? 0;
  const scale = recipe && qtyNum > 0 ? qtyNum / recipe.yieldQty : 0;

  const preview = useMemo(() => {
    if (!recipe || scale <= 0) return [];
    return recipe.lines.map((l) => {
      const scaledQty = l.qty * scale;
      const currentQty = currentBranchId
        ? getCachedBranchProductQty(l.productId, currentBranchId)
        : null;
      return {
        ...l,
        scaledQty,
        unitLabel: uomById.get(l.productId) ?? 'u.',
        currentQty,
        nextQty: currentQty != null ? currentQty + scaledQty : null,
      };
    });
  }, [recipe, scale, uomById, currentBranchId]);

  /** La madre es la única línea que descuenta → la única que puede quedar en negativo. */
  const motherStock = useMemo(() => {
    if (!currentBranchId || qtyNum <= 0) return null;
    const currentQty = getCachedBranchProductQty(productId, currentBranchId);
    return { currentQty, nextQty: currentQty - qtyNum };
  }, [currentBranchId, productId, qtyNum]);

  const motherGoesNegative = motherStock != null && motherStock.nextQty < 0;

  const doDisassemble = async () => {
    if (!currentBranchId || !recipe) return;
    setSaving(true);
    try {
      await applyRecipeDisassemble({
        batchId: crypto.randomUUID(),
        branchId: currentBranchId,
        productId,
        batchQty: qtyNum,
        performedByUserId: userId,
      });
      showSuccess('Fraccionamiento listo', `Cortaste ${formatQtyCompact(qtyNum)} de ${productName}.`);
      navigation.goBack();
    } catch (error) {
      presentUnexpectedFailure({
        error,
        module: 'catalog',
        op: 'recipes.disassemble',
        userMessage: 'No pude fraccionar. Reintentá.',
      });
    } finally {
      setSaving(false);
      setNegStockConfirmOpen(false);
    }
  };

  /** Si la madre queda en negativo → confirm explícito antes del RPC. */
  const confirm = async () => {
    if (!currentBranchId) {
      showError('Sucursal', 'Elegí una sucursal.');
      return;
    }
    if (!recipe) {
      showError('Fraccionamiento', 'Definí el fraccionamiento antes.');
      return;
    }
    if (qtyNum <= 0) {
      showError('Cantidad', 'Poné cuánto querés fraccionar.');
      return;
    }
    if (motherGoesNegative) {
      setNegStockConfirmOpen(true);
      return;
    }
    await doDisassemble();
  };

  if (!hasRecipes) {
    return (
      <OperationalScrollScreen
        layoutWide={layoutWide}
        contentContainerStyle={styles.content}
        body={
          <ModuleEmptyState
            title="Recetas apagadas"
            subtitle="Prendé Recetas en Funciones del negocio."
          />
        }
      />
    );
  }

  if (loading) {
    return (
      <OperationalScrollScreen
        layoutWide={layoutWide}
        contentContainerStyle={styles.content}
        body={
          <View style={styles.centered}>
            <ActivityIndicator color={ticketColors.ink} />
          </View>
        }
      />
    );
  }

  if (!recipe) {
    return (
      <OperationalScrollScreen
        layoutWide={layoutWide}
        contentContainerStyle={styles.content}
        header={
          <TicketModuleHeader kicker="CATÁLOGO" title="Fraccionar" meta={productName} />
        }
        body={
          <ModuleEmptyState
            title="Falta la receta"
            subtitle="Definí en qué se parte este producto."
            primaryAction={{
              label: 'Abrir receta',
              onPress: () =>
                navigation.replace('ProductRecipe', {
                  productId,
                  productName,
                  kind: 'disassemble',
                }),
            }}
          />
        }
      />
    );
  }

  const header = (
    <TicketModuleHeader kicker="CATÁLOGO" title="Fraccionar" meta={productName} />
  );

  const body = (
    <>
      <OperationalConfirmModal
        visible={negStockConfirmOpen}
        title="Stock en negativo"
        entityName={productName}
        message={
          motherStock
            ? `El stock va a quedar en negativo: ${productName} queda en ${formatQtyCompact(motherStock.nextQty)} ${motherUnit}.`
            : ''
        }
        confirmLabel="Fraccionar igual"
        destructive
        loading={saving}
        onClose={() => setNegStockConfirmOpen(false)}
        onConfirm={() => void doDisassemble()}
      />

      <PaperSheet padded>
        <Text style={styles.sheetBody}>
          Por cada {formatQtyCompact(recipe.yieldQty)} {motherUnit} de {productName}, sumás los
          cortes. Al doble, baja el doble de madre.
        </Text>
      </PaperSheet>

      <PaperSheet padded>
        <Text style={styles.fieldLabel}>Cantidad a fraccionar</Text>
        <Text style={styles.fieldHint}>
          {motherUnit} de {productName}.
        </Text>
        <View style={styles.qtyRow}>
          <NumericTextInput
            kind="signedDecimal"
            style={styles.qtyInput}
            value={batchQty}
            onChangeText={setBatchQty}
            placeholderTextColor={ticketColors.ink3}
            testID="catalog.disassemble.qty"
          />
          <Text style={styles.qtyUnit}>{motherUnit}</Text>
        </View>
      </PaperSheet>

      <PaperSheet padded>
        <Text style={styles.sheetTitle}>Qué va a pasar</Text>
        <Text style={[styles.previewLine, styles.previewOut]}>
          − {formatQtyCompact(qtyNum > 0 ? qtyNum : 0)} {motherUnit} · {productName}
        </Text>
        {motherStock ? (
          <Text style={[styles.stockLine, motherGoesNegative && styles.stockLineNeg]}>
            Tenés {formatQtyCompact(motherStock.currentQty)} {motherUnit} → queda en{' '}
            {formatQtyCompact(motherStock.nextQty)} {motherUnit}
          </Text>
        ) : null}
        {preview.map((l) => (
          <View key={l.productId}>
            <Text style={styles.previewLine}>
              {l.isShrinkage ? 'Merma' : '+'} {formatQtyCompact(l.scaledQty)} {l.unitLabel} ·{' '}
              {l.productName ?? l.productId}
            </Text>
            {l.currentQty != null && l.nextQty != null ? (
              <Text style={styles.stockLine}>
                Tenés {formatQtyCompact(l.currentQty)} {l.unitLabel} → queda en{' '}
                {formatQtyCompact(l.nextQty)} {l.unitLabel}
              </Text>
            ) : null}
          </View>
        ))}
        {motherGoesNegative ? (
          <Text style={styles.warnLine}>La madre queda en negativo. Confirmá para seguir.</Text>
        ) : null}
      </PaperSheet>

      <PaperSheet padded>
        <TouchableOpacity
          style={[styles.saveBtn, saving && styles.saveBtnDisabled]}
          onPress={() => void confirm()}
          disabled={saving}
          accessibilityRole="button"
          testID="catalog.disassemble.confirm"
        >
          <Text style={styles.saveBtnText}>
            {saving ? 'Fraccionando…' : 'Fraccionar stock'}
          </Text>
        </TouchableOpacity>
      </PaperSheet>
    </>
  );

  return (
    <OperationalScrollScreen
      layoutWide={layoutWide}
      header={header}
      body={body}
      contentContainerStyle={styles.content}
      testID="catalog.disassemble.screen"
    />
  );
};

const styles = StyleSheet.create({
  content: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
    paddingBottom: spacing.xl,
  },
  centered: { minHeight: 160, alignItems: 'center', justifyContent: 'center' },
  sheetTitle: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 15,
    color: ticketColors.ink,
    marginBottom: spacing.sm,
  },
  sheetBody: {
    fontFamily: ticketFonts.sans,
    fontSize: 13,
    lineHeight: 18,
    color: ticketColors.ink2,
  },
  fieldLabel: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 13,
    color: ticketColors.ink,
    marginBottom: 4,
  },
  fieldHint: {
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    color: ticketColors.ink3,
    marginBottom: spacing.sm,
  },
  qtyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    alignSelf: 'flex-start',
  },
  qtyInput: {
    width: 112,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    color: ticketColors.ink,
    backgroundColor: ticketColors.tile,
    fontFamily: ticketFonts.sans,
    fontSize: 16,
  },
  qtyUnit: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 14,
    color: ticketColors.ink2,
  },
  previewLine: {
    fontFamily: ticketFonts.sans,
    fontSize: 14,
    color: ticketColors.ink2,
    marginBottom: 4,
  },
  previewOut: {
    fontWeight: '700',
    color: ticketColors.ink,
    marginBottom: spacing.xs,
  },
  stockLine: {
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    color: ticketColors.ink3,
    marginLeft: 12,
    marginBottom: 6,
  },
  stockLineNeg: { color: ticketColors.bad, fontWeight: '700' },
  warnLine: {
    marginTop: spacing.sm,
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    color: ticketColors.bad,
    fontWeight: '700',
  },
  saveBtn: {
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  saveBtnDisabled: { opacity: 0.6 },
  saveBtnText: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 15,
    color: ticketColors.goldInk,
  },
});
